import React, { useState, useEffect } from 'react'

import { DownloadOutlined } from '@ant-design/icons'
import { Button, Alert, Tag, Skeleton, Row, Col } from 'antd'
import { useTranslation } from 'react-i18next'

import { formatDateTime } from '../../utils/dateTime'

/**
 * 影片預覽組件（共用）
 * 用於訂單詳情、退貨單詳情等頁面顯示驗收/出貨影片
 *
 * @param {string} orderId - 資源 ID（訂單、退貨單等）
 * @param {Object} orderService - 服務物件（需提供 getVideos(id) 方法）
 */
export default function VideoPreview({ orderId, orderService }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [videos, setVideos] = useState([])

  useEffect(() => {
    if (!orderId || !orderService) return

    loadVideos()
  }, [orderId, orderService])

  const loadVideos = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await orderService.getVideos(orderId)

      if (data.success) {
        setVideos(data.data.videos || [])
      } else {
        setError(data.message || t('common.error.load_failed'))
      }
    } catch (err) {
      console.error('Failed to load videos:', err)
      setError(t('common.error.network_error'))
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = (video) => {
    const link = document.createElement('a')
    link.href = video.public_url
    link.download = `video_${video.id}.webm`
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Loading 狀態
  if (loading) {
    return (
      <Row gutter={[16, 16]}>
        {[1, 2].map((i) => (
          <Col key={i} xs={24} sm={12} md={12} lg={8}>
            <Skeleton.Image
              active
              style={{
                width: '100%',
                height: '220px',
                borderRadius: '8px'
              }}
            />
            <div style={{ marginTop: '8px' }}>
              <Skeleton active paragraph={{ rows: 1, width: ['60%'] }} title={false} />
            </div>
          </Col>
        ))}
      </Row>
    )
  }

  // Error 狀態
  if (error) {
    return (
      <Alert
        message={t('common.error.load_failed')}
        description={error}
        type="error"
        showIcon
        size="small"
      />
    )
  }

  // 沒有影片
  if (videos.length === 0) {
    return <span style={{ color: '#999' }}>-</span>
  }

  // 影片列表
  return (
    <Row gutter={[16, 16]}>
      {videos.map((video, index) => (
        <Col key={video.id} xs={24} sm={12} md={12} lg={8}>
          <div>
            {/* 影片播放器 */}
            <div style={{
              border: '1px solid #f0f0f0',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: '#000',
              marginBottom: '8px'
            }}>
              <video
                controls
                preload="metadata"
                style={{
                  width: '100%',
                  aspectRatio: '16 / 10',
                  display: 'block'
                }}
              >
                <source src={video.public_url} type="video/webm" />
                <source src={video.public_url} type="video/mp4" />
                {t('shared.video_preview.not_supported', { defaultValue: '您的瀏覽器不支援影片播放' })}
              </video>
            </div>

            {/* 影片資訊 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 500, fontSize: '13px' }}>
                  {t('shared.video_preview.item', { defaultValue: '影片 {{index}}', index: index + 1 })}
                </span>
                {video.parcel_id && (
                  <Tag color="blue" style={{ fontSize: '11px', margin: 0 }}>
                    Parcel: {video.parcel_id}
                  </Tag>
                )}
                <span style={{ color: '#999', fontSize: '12px' }}>
                  {video.created_at ? formatDateTime(video.created_at) : ''}
                </span>
              </div>

              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(video)}
              >
                {t('shared.video_preview.download', { defaultValue: '下載' })}
              </Button>
            </div>
          </div>
        </Col>
      ))}
    </Row>
  )
}
